import { Button } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { ExternalLink } from 'lucide-react';
import { DateCell } from '@/components/data-table/cells';
import type { UserClientCategorisation } from '@/services/api/client-categorisation';
import { capitalizeLower, shortUuid } from '@/utils/string';

/**
 * Legacy path kept identical: until the phase-7 user console lands in
 * v2 this is a cross-app link into the legacy portal — always a new tab so it
 * works both before and after cutover.
 */
export const openUserInNewTab = (pid: string) => window.open(`/operations/users/${pid}`, '_blank');

// Enrichment misses are normal: legacy rendered empty cells; v2 renders an em dash
const MissingValue = () => <span className="text-muted-foreground">—</span>;
MissingValue.displayName = 'MissingValue';

const PidCell = ({ pid }: { pid: string }) => (
  <span className="inline-flex items-center gap-1 whitespace-nowrap font-mono text-xs" title={pid}>
    {shortUuid(pid)}
    <Button
      aria-label={`Open user ${pid} in a new tab`}
      className="size-6"
      onClick={(event) => {
        // The surrounding row click opens the same link — don't fire it twice
        event.stopPropagation();
        openUserInNewTab(pid);
      }}
      size="icon"
      type="button"
      variant="ghost"
    >
      <ExternalLink className="size-3.5" />
    </Button>
  </span>
);
PidCell.displayName = 'PidCell';

/** ALL non-sortable (legacy parity) */
export const CLIENT_CATEGORISATION_COLUMNS: ColumnDef<UserClientCategorisation, unknown>[] = [
  {
    accessorKey: 'pid',
    header: 'PID',
    enableSorting: false,
    cell: ({ row }) => <PidCell pid={row.original.pid} />,
  },
  {
    accessorKey: 'email',
    header: 'Email',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.email ? (
        <span className="block max-w-64 truncate" title={row.original.email}>
          {row.original.email}
        </span>
      ) : (
        <MissingValue />
      ),
  },
  {
    accessorKey: 'date',
    header: 'Date',
    enableSorting: false,
    cell: ({ row }) => <DateCell value={row.original.date} />,
  },
  {
    accessorKey: 'segment',
    header: 'Segment',
    enableSorting: false,
    cell: ({ row }) => (row.original.segment ? capitalizeLower(row.original.segment) : <MissingValue />),
  },
  {
    accessorKey: 'categorisation',
    header: 'Categorisation',
    enableSorting: false,
    cell: ({ row }) => capitalizeLower(row.original.categorisation),
  },
];
